import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { ProgramsService } from '../../../services/patientmr/programs.service';
import { describeApiError } from '../../../utils/apiErrors';
import {
  CODE_STATUSES, CODE_STATUS_LABELS, PLACES_OF_CARE, PLACE_OF_CARE_LABELS, WEEKDAYS,
} from '../../../dtos/patientmr/programs.dto';
import type { HospiceElection, PalliativeProgram, PatientPrograms, PpeccAttendance, ProgramName } from '../../../dtos/patientmr/programs.dto';

const input = 'w-full rounded border border-slate-300 px-2 py-1 text-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500 disabled:bg-slate-100';
const label = 'block text-xs font-medium text-slate-600 mb-0.5';

const Text: React.FC<{ id: string; name: string; value: string | null | undefined; onChange: (v: string) => void; disabled?: boolean; type?: string; area?: boolean; required?: boolean }> = ({
  id, name, value, onChange, disabled, type = 'text', area = false, required = false,
}) => (
  <div>
    <label htmlFor={id} className={label}>{name}{required && <span className="text-red-600"> *</span>}</label>
    {area
      ? <textarea id={id} rows={2} className={`${input} resize-none`} disabled={disabled} value={value || ''} onChange={(e) => onChange(e.target.value)} />
      : <input id={id} type={type} className={input} disabled={disabled} value={value || ''} onChange={(e) => onChange(e.target.value)} />}
  </div>
);

const Select: React.FC<{ id: string; name: string; value: string | null | undefined; options: ReadonlyArray<{ value: string; label: string }>; onChange: (v: string) => void; disabled?: boolean }> = ({
  id, name, value, options, onChange, disabled,
}) => (
  <div>
    <label htmlFor={id} className={label}>{name}</label>
    <select id={id} className={input} disabled={disabled} value={value || ''} onChange={(e) => onChange(e.target.value)}>
      <option value="">Select…</option>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

const emptyPalliative = (): PalliativeProgram => ({ active: true, startDate: '', enrollingClinician: '', reason: '', goalsOfCare: {}, comfortPlan: {}, bereavement: {} });
const emptyHospice = (): HospiceElection => ({ electionDate: '', hospiceName: '', hospiceNpi: '', revocationDate: '' });
const emptyPpecc = (): PpeccAttendance => ({ centerName: '', centerNpi: '', startDate: '', endDate: '', attendanceDays: [], hoursPerDay: null });

/** Blank strings become null so the server stores "not given" */
const nulls = <T,>(v: T): T => JSON.parse(JSON.stringify(v, (_k, x) => (x === '' ? null : x)));

const Section: React.FC<{ title: string; summary: string; open: boolean; onToggle: () => void; children: React.ReactNode; testId: string }> = ({ title, summary, open, onToggle, children, testId }) => (
  <div className="rounded-lg border border-slate-200" data-testid={testId}>
    <button type="button" onClick={onToggle} className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-slate-50">
      <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">{open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}{title}</span>
      <span className="text-xs text-slate-500">{summary}</span>
    </button>
    {open && <div className="space-y-2 border-t border-slate-100 px-3 py-3">{children}</div>}
  </div>
);

/**
 * Phase 20F: palliative care programme (goals of care, code status, comfort plan, bereavement),
 * hospice election and PPECC attendance. Staff-only records (plan D8).
 */
const ProgramsPanel: React.FC<{ patientId: string; programs: PatientPrograms | null; onSaved: (p: PatientPrograms) => void; readOnly?: boolean }> = ({
  patientId, programs, onSaved, readOnly = false,
}) => {
  const [open, setOpen] = useState<ProgramName | null>(null);
  const [pal, setPal] = useState<PalliativeProgram>(emptyPalliative);
  const [hos, setHos] = useState<HospiceElection>(emptyHospice);
  const [ppe, setPpe] = useState<PpeccAttendance>(emptyPpecc);
  const [saving, setSaving] = useState<ProgramName | null>(null);
  const [message, setMessage] = useState<{ program: ProgramName; text: string; error: boolean } | null>(null);

  useEffect(() => {
    setPal({ ...emptyPalliative(), ...(programs?.palliative?.data || {}) });
    setHos({ ...emptyHospice(), ...(programs?.hospice?.data || {}) });
    setPpe({ ...emptyPpecc(), ...(programs?.ppecc?.data || {}) });
  }, [programs]);

  const save = async <T,>(program: ProgramName, data: T) => {
    setSaving(program);
    setMessage(null);
    try {
      const saved = await ProgramsService.save(patientId, program, nulls(data), programs?.[program]?.version ?? null);
      onSaved({ ...(programs || { palliative: null, hospice: null, ppecc: null }), [program]: saved } as PatientPrograms);
      setMessage({ program, text: 'Saved', error: false });
    } catch (e) {
      setMessage({ program, text: describeApiError(e, 'The record could not be saved'), error: true });
    } finally {
      setSaving(null);
    }
  };
  const toggle = (p: ProgramName) => setOpen(open === p ? null : p);
  const footer = (program: ProgramName, onSave: () => void) => (
    <div className="flex items-center justify-end gap-3">
      {message?.program === program && <span className={`text-xs ${message.error ? 'text-red-600' : 'text-emerald-700'}`} role="status">{message.text}</span>}
      {!readOnly && (
        <button type="button" onClick={onSave} disabled={saving === program}
          className="rounded bg-slate-800 px-3 py-1 text-sm text-white hover:bg-slate-700 disabled:opacity-50">
          {saving === program ? 'Saving…' : 'Save'}
        </button>
      )}
    </div>
  );
  const p = (id: string) => `program-${id}`;

  return (
    <div className="space-y-2" data-testid="programs-panel">
      <h3 className="text-sm font-semibold text-slate-800">Programmes</h3>

      <Section testId="program-palliative" title="Palliative care" open={open === 'palliative'} onToggle={() => toggle('palliative')}
        summary={ProgramsService.palliativeActive(programs) ? `In the programme since ${programs?.palliative?.data.startDate}` : 'Not enrolled'}>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
          <Text id={p('pal-start')} name="Start date" type="date" required value={pal.startDate} disabled={readOnly} onChange={(v) => setPal({ ...pal, startDate: v })} />
          <Text id={p('pal-end')} name="End date" type="date" value={pal.endDate} disabled={readOnly} onChange={(v) => setPal({ ...pal, endDate: v, active: !v })} />
          <Text id={p('pal-clinician')} name="Enrolling clinician" required value={pal.enrollingClinician} disabled={readOnly} onChange={(v) => setPal({ ...pal, enrollingClinician: v })} />
        </div>
        <Text id={p('pal-reason')} name="Reason" area required value={pal.reason} disabled={readOnly} onChange={(v) => setPal({ ...pal, reason: v })} />
        <Select id={p('pal-code')} name="Code status" value={pal.codeStatus} disabled={readOnly}
          options={CODE_STATUSES.map((c) => ({ value: c, label: CODE_STATUS_LABELS[c] }))} onChange={(v) => setPal({ ...pal, codeStatus: (v || null) as PalliativeProgram['codeStatus'] })} />
        <p className="pt-1 text-xs font-semibold uppercase text-slate-500">Goals of care</p>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          <Text id={p('goc-date')} name="Conversation date" type="date" value={pal.goalsOfCare?.conversationDate} disabled={readOnly} onChange={(v) => setPal({ ...pal, goalsOfCare: { ...pal.goalsOfCare, conversationDate: v } })} />
          <Text id={p('goc-who')} name="Participants" value={pal.goalsOfCare?.participants} disabled={readOnly} onChange={(v) => setPal({ ...pal, goalsOfCare: { ...pal.goalsOfCare, participants: v } })} />
        </div>
        <Text id={p('goc-goals')} name="Patient and family goals" area value={pal.goalsOfCare?.patientFamilyGoals} disabled={readOnly} onChange={(v) => setPal({ ...pal, goalsOfCare: { ...pal.goalsOfCare, patientFamilyGoals: v } })} />
        <Text id={p('goc-decisions')} name="Decisions and preferences" area value={pal.goalsOfCare?.decisions} disabled={readOnly} onChange={(v) => setPal({ ...pal, goalsOfCare: { ...pal.goalsOfCare, decisions: v } })} />
        <p className="pt-1 text-xs font-semibold uppercase text-slate-500">Comfort plan</p>
        <Text id={p('cp-symptoms')} name="Symptom plan" area value={pal.comfortPlan?.symptomPlan} disabled={readOnly} onChange={(v) => setPal({ ...pal, comfortPlan: { ...pal.comfortPlan, symptomPlan: v } })} />
        <Text id={p('cp-emergency')} name="Emergency plan" area value={pal.comfortPlan?.emergencyPlan} disabled={readOnly} onChange={(v) => setPal({ ...pal, comfortPlan: { ...pal.comfortPlan, emergencyPlan: v } })} />
        <Select id={p('cp-place')} name="Preferred place of care" value={pal.comfortPlan?.preferredPlaceOfCare} disabled={readOnly}
          options={PLACES_OF_CARE.map((c) => ({ value: c, label: PLACE_OF_CARE_LABELS[c] }))} onChange={(v) => setPal({ ...pal, comfortPlan: { ...pal.comfortPlan, preferredPlaceOfCare: (v || null) as never } })} />
        <p className="pt-1 text-xs font-semibold uppercase text-slate-500">Bereavement</p>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          <Text id={p('bv-date')} name="Follow-up date" type="date" value={pal.bereavement?.followUpDate} disabled={readOnly} onChange={(v) => setPal({ ...pal, bereavement: { ...pal.bereavement, followUpDate: v } })} />
          <Text id={p('bv-notes')} name="Notes" value={pal.bereavement?.notes} disabled={readOnly} onChange={(v) => setPal({ ...pal, bereavement: { ...pal.bereavement, notes: v } })} />
        </div>
        {footer('palliative', () => save('palliative', pal))}
      </Section>

      <Section testId="program-hospice" title="Hospice election" open={open === 'hospice'} onToggle={() => toggle('hospice')}
        summary={ProgramsService.hospiceActive(programs) ? `Elected ${programs?.hospice?.data.electionDate}` : 'None'}>
        <p className="text-xs text-slate-600">While hospice is elected, care related to the terminal condition may be the hospice&apos;s responsibility. STAR and STAR Health members move to Medicaid fee-for-service.</p>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          <Text id={p('hos-date')} name="Election date" type="date" required value={hos.electionDate} disabled={readOnly} onChange={(v) => setHos({ ...hos, electionDate: v })} />
          <Text id={p('hos-revoked')} name="Revocation date" type="date" value={hos.revocationDate} disabled={readOnly} onChange={(v) => setHos({ ...hos, revocationDate: v })} />
          <Text id={p('hos-name')} name="Hospice" required value={hos.hospiceName} disabled={readOnly} onChange={(v) => setHos({ ...hos, hospiceName: v })} />
          <Text id={p('hos-npi')} name="Hospice NPI" required value={hos.hospiceNpi} disabled={readOnly} onChange={(v) => setHos({ ...hos, hospiceNpi: v })} />
        </div>
        {footer('hospice', () => save('hospice', hos))}
      </Section>

      <Section testId="program-ppecc" title="PPECC attendance" open={open === 'ppecc'} onToggle={() => toggle('ppecc')}
        summary={programs?.ppecc?.data?.centerName ? `${programs.ppecc.data.centerName} · ${programs.ppecc.data.attendanceDays.join(', ')}` : 'None'}>
        <p className="text-xs text-slate-600">PDN shifts on attendance days are flagged so they are scheduled outside the centre&apos;s hours.</p>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          <Text id={p('pp-name')} name="Centre" required value={ppe.centerName} disabled={readOnly} onChange={(v) => setPpe({ ...ppe, centerName: v })} />
          <Text id={p('pp-npi')} name="Centre NPI" required value={ppe.centerNpi} disabled={readOnly} onChange={(v) => setPpe({ ...ppe, centerNpi: v })} />
          <Text id={p('pp-start')} name="Start date" type="date" required value={ppe.startDate} disabled={readOnly} onChange={(v) => setPpe({ ...ppe, startDate: v })} />
          <Text id={p('pp-end')} name="End date" type="date" value={ppe.endDate} disabled={readOnly} onChange={(v) => setPpe({ ...ppe, endDate: v })} />
          <Text id={p('pp-hours')} name="Hours per day" type="number" value={ppe.hoursPerDay === null || ppe.hoursPerDay === undefined ? '' : String(ppe.hoursPerDay)} disabled={readOnly}
            onChange={(v) => setPpe({ ...ppe, hoursPerDay: v === '' ? null : Number(v) })} />
        </div>
        <fieldset>
          <legend className={label}>Attendance days<span className="text-red-600"> *</span></legend>
          <div className="flex flex-wrap gap-3">
            {WEEKDAYS.map((d) => (
              <label key={d} className="flex items-center gap-1 text-sm">
                <input type="checkbox" disabled={readOnly} checked={ppe.attendanceDays.includes(d)}
                  onChange={(e) => setPpe({ ...ppe, attendanceDays: e.target.checked ? [...ppe.attendanceDays, d] : ppe.attendanceDays.filter((x) => x !== d) })} />
                {d.charAt(0) + d.slice(1).toLowerCase()}
              </label>
            ))}
          </div>
        </fieldset>
        {footer('ppecc', () => save('ppecc', ppe))}
      </Section>
    </div>
  );
};

export default ProgramsPanel;
